import React, { useCallback, useState, useEffect } from "react";
import "./roadConditions.css";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { isAdmin } from "../../utils/roles";
import usePullToRefresh from "../../hooks/usePullToRefresh";

// Filter chips (category values match the RoadAlert schema enum).
const CATEGORIES = [
  { v: "all", l: "All" },
  { v: "closure", l: "Closures" },
  { v: "diversion", l: "Diversions" },
  { v: "accident", l: "Accidents" },
  { v: "fog", l: "Fog" },
  { v: "weather", l: "Weather" },
  { v: "congestion", l: "Traffic" },
];

const CATEGORY_ICON = {
  closure: "🚧",
  diversion: "↪️",
  accident: "💥",
  fog: "🌫️",
  weather: "🌧️",
  congestion: "🚗",
  hazard: "⚠️",
  other: "ℹ️",
};

const SOURCE_LABEL = {
  nhmp: "NHMP",
  pmd: "Met Dept",
  caotg: "Champs Alerts",
  other: "Community",
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

// How long since the last update before a card is flagged "may be outdated".
// Scrapes are re-confirmed every ~10 min, so one not refreshed in 30 min has
// likely cleared (lingering in its TTL grace window) or the scraper is down.
// WhatsApp reports are one-off and never re-confirmed, so they simply age out.
// We keep showing these — stale data beats an empty feed — but visibly dim +
// badge them so nobody treats old info as live.
const STALE_AFTER_MIN = { scrape: 30, whatsapp: 180 };

function isStale(a) {
  if (!a || !a.updatedAt) return false;
  const threshold = STALE_AFTER_MIN[a.sourceType] || 60;
  return Date.now() - new Date(a.updatedAt).getTime() > threshold * 60000;
}

const RoadConditions = ({ isLoggedIn, loginUser, onLogout }) => {
  const [alerts, setAlerts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const navigate = useNavigate();

  const getAlerts = useCallback(async (cat = "all") => {
    try {
      const params = cat && cat !== "all" ? { category: cat } : {};
      const response = await axios.get("/api/v1/road-conditions", { params });
      setAlerts(response.data.alerts || []);
      setError("");
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        navigate("/login");
      } else if (err.response) {
        setError(err.response.data.message || "Could not load road conditions.");
        setAlerts([]);
      } else {
        setError("An error occurred while loading road conditions.");
        setAlerts([]);
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout, navigate]);

  const handleRefresh = useCallback(async () => {
    await getAlerts(category);
  }, [getAlerts, category]);

  const { isRefreshing, pullDistance, containerRef } = usePullToRefresh(handleRefresh);

  // Fetch + poll every 30s while the tab is visible.
  useEffect(() => {
    let intervalId;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !intervalId) {
        intervalId = setInterval(() => getAlerts(category), 30000);
      } else if (document.visibilityState !== "visible" && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    getAlerts(category);
    if (document.visibilityState === "visible") {
      intervalId = setInterval(() => getAlerts(category), 30000);
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [getAlerts, category]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
    } else {
      localStorage.setItem("lastSeenRoadConditions", new Date().toISOString());
    }
  }, [isLoggedIn, navigate]);

  const handleHide = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.put(`/api/v1/road-conditions/${id}/hide`);
      setAlerts((prev) => prev.filter((a) => a._id !== id));
    } catch (_) {
      /* non-fatal */
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.delete(`/api/v1/road-conditions/${id}`);
      setAlerts((prev) => prev.filter((a) => a._id !== id));
    } catch (_) {
      /* non-fatal */
    }
  };

  const admin = isLoggedIn && isAdmin(loginUser);

  return (
    <div className="sr-roads" ref={containerRef}>
      <Helmet>
        <title>SastiRide - Live Road Conditions Pakistan (Motorways & Highways)</title>
        <meta name="description" content="Real-time motorway and highway conditions across Pakistan — closures, diversions, accidents, fog and traffic — aggregated from NHMP, the Met Department and community alerts." />
      </Helmet>

      {/* ── Dark top section ── */}
      <div className="sr-roads__top">
        <div className="sr-roads__top-content">
          <div className="sr-roads__header">
            <h1 className="sr-roads__title">Road Conditions</h1>
            <p className="sr-roads__subtitle">Live motorway &amp; highway updates across Pakistan</p>
          </div>
        </div>
      </div>

      {/* ── White body ── */}
      <div className="sr-roads__body">
        {/* Category filter */}
        <div className="sr-roads__tabs">
          {CATEGORIES.map((t) => (
            <button
              key={t.v}
              className={`sr-roads__tab ${category === t.v ? "sr-roads__tab--active" : ""}`}
              onClick={() => setCategory(t.v)}
            >
              {t.l}
            </button>
          ))}
        </div>

        {(pullDistance > 0 || isRefreshing) && (
          <div className="sr-roads__pull-indicator" style={{ height: pullDistance }}>
            {isRefreshing ? "Refreshing..." : "Pull to refresh"}
          </div>
        )}

        <div className="sr-roads__list">
          {error !== "" && <div className="sr-roads__error">{error}</div>}

          {loading && alerts.length === 0 && error === "" &&
            [1, 2, 3].map((i) => (
              <div className="sr-roads__card sr-roads__card--skeleton" key={i}>
                <div className="sr-roads__skeleton sr-roads__skeleton--line" />
                <div className="sr-roads__skeleton sr-roads__skeleton--line sr-roads__skeleton--short" />
              </div>
            ))
          }

          {!loading && error === "" && alerts.length === 0 && (
            <div className="sr-roads__empty">
              <p className="sr-roads__empty-icon">✅</p>
              <p className="sr-roads__empty-text">All clear — no active road conditions reported right now.</p>
            </div>
          )}

          {error === "" && alerts.map((a) => {
            const stale = isStale(a);
            return (
            <div className={`sr-roads__card sr-roads__card--${a.severity}${stale ? " sr-roads__card--stale" : ""}`} key={a._id}>
              <div className="sr-roads__card-main">
                <span className="sr-roads__card-icon" aria-hidden="true">
                  {CATEGORY_ICON[a.category] || CATEGORY_ICON.other}
                </span>
                <div className="sr-roads__card-content">
                  <div className="sr-roads__card-head">
                    {a.road && <span className="sr-roads__card-road">{a.road}</span>}
                    {a.area && <span className="sr-roads__card-area">{a.area}</span>}
                  </div>
                  <p className="sr-roads__card-summary">{a.summary}</p>
                  <div className="sr-roads__card-meta">
                    <span className={`sr-roads__card-source sr-roads__card-source--${a.source}`}>
                      {SOURCE_LABEL[a.source] || "Source"}
                    </span>
                    {a.posterName && <span className="sr-roads__card-poster">{a.posterName}</span>}
                    {stale && <span className="sr-roads__card-stale-badge">⚠ May be outdated</span>}
                    <span className="sr-roads__card-time">{timeAgo(a.updatedAt)}</span>
                  </div>
                </div>
              </div>
              {admin && (
                <div className="sr-roads__card-admin">
                  <button className="sr-roads__admin-btn" onClick={(e) => handleHide(e, a._id)}>Hide</button>
                  <button className="sr-roads__admin-btn sr-roads__admin-btn--danger" onClick={(e) => handleDelete(e, a._id)}>Delete</button>
                </div>
              )}
            </div>
            );
          })}
        </div>

        <p className="sr-roads__disclaimer">
          Aggregated from NHMP, Pakistan Met Department and community alerts. Always confirm before you travel — call the Motorway Police helpline <strong>130</strong> for the latest.
        </p>
      </div>
    </div>
  );
};

export default RoadConditions;
